import {
  CAlert,
  CButton,
  CButtonGroup,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormSwitch,
  CLoadingButton,
  CRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilUser } from '@coreui/icons'
import React, { useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import PlayerService from 'src/service/PlayerService'
import ToastComponent from 'src/components/common/TaostComponent'
import PreviewImage from '../PreviewImage'

const SUPPORTED_FORMATS = ['image/jpg', 'image/png', 'image/jpeg', 'image/gif']

const AddForm = ({ visible, onClose, fetchUsers }) => {
  const [loader, setLoader] = useState(false)
  const [position, setPosition] = useState()
  const [team, setTeam] = useState()
  const [playerValue, setPlayerValue] = useState([])
  const [batStyle, setBatStyle] = useState()
  const [bowlStyle, setBowlStyle] = useState()
  const [imageMode, setImageMode] = useState('avatar')
  const [errorMessages, setErrorMessages] = useState([])
  const keepOpenRef = useRef(false)

  useEffect(() => {
    if (visible) {
      PlayerService.getTeamPositionValueBatBowlStyle().then((result) => {
        setPosition(result.position)
        setTeam(result.teamList)
        setBatStyle(result.batStyle)
        setBowlStyle(result.bowlStyle)
        setPlayerValue(result.fantasy_values)
      })
    }
  }, [visible])

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .required('First Name is required')
      .max(50, '50 Character Limit is allowed.'),
    last_name: Yup.string()
      .required('Last Name is required')
      .max(50, '50 Character Limit is allowed.'),
    team: Yup.string(),
    position: Yup.string().required('Position is required'),
    value: Yup.string().required('Value is required'),
    image: Yup.mixed()
      .nullable(true)
      .test('fileSize', 'File size too large, max file size is 5 Mb', (file) => {
        if (file) {
          return file.size <= 5500000
        } else {
          return true
        }
      })
      .test(
        'type',
        'Invalid file format selection',
        (value) => !value || (value && SUPPORTED_FORMATS.includes(value?.type)),
      ),
  })

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      position: '',
      value: '',
      team: '',
      bowl_style: '',
      bat_style: '',
      image: null,
      is_active: true,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data, actions) => {
      const formData = new FormData()
      formData.append('bat_style', data.bat_style)
      formData.append('bowl_style', data.bowl_style)
      formData.append('first_name', data.first_name)
      formData.append('last_name', data.last_name)
      formData.append('position', data.position)
      formData.append('team', data.team)
      formData.append('value', data.value)
      formData.append('is_active', data.is_active ? 1 : 0)
      if (imageMode === 'upload' && data.image) {
        formData.append('image', data.image)
      }
      setLoader(true)
      PlayerService.savePlayer(formData)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers()
            ToastComponent(res.message, 'success')
            actions.resetForm()
            setImageMode('avatar')
            if (!keepOpenRef.current) {
              onClose()
            }
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
          setLoader(false)
        })
        .catch(() => {
          ToastComponent('Something went wrong', 'error')
          setLoader(false)
        })
    },
  })

  const handleClose = () => {
    formik.resetForm()
    setErrorMessages([])
    setImageMode('avatar')
    onClose()
  }

  const submitForm = (keepOpen) => {
    keepOpenRef.current = keepOpen
    formik.handleSubmit()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 640 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Add Player</COffcanvasTitle>
          <div className="text-medium-emphasis small">Create a new player record.</div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1" onSubmit={formik.handleSubmit}>
          <CCol xs={12}>
            <h6 className="fw-bold mb-0">Required Details</h6>
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="first_name">First Name *</CFormLabel>
            <CFormInput
              placeholder="First Name"
              className={
                'form-control' +
                (formik.errors.first_name && formik.touched.first_name ? ' is-invalid' : '')
              }
              value={formik.values.first_name}
              onChange={formik.handleChange}
              aria-label="first_name"
              id="first_name"
            />
            {formik.errors.first_name && formik.touched.first_name && (
              <CFormFeedback invalid>{formik.errors.first_name}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="last_name">Last Name *</CFormLabel>
            <CFormInput
              placeholder="Last Name"
              className={
                'form-control' +
                (formik.errors.last_name && formik.touched.last_name ? ' is-invalid' : '')
              }
              value={formik.values.last_name}
              onChange={formik.handleChange}
              aria-label="last_name"
              id="last_name"
            />
            {formik.errors.last_name && formik.touched.last_name && (
              <CFormFeedback invalid>{formik.errors.last_name}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="team">Team</CFormLabel>
            <CFormSelect
              aria-label="Select Team"
              name="team"
              className={
                'form-control' + (formik.errors.team && formik.touched.team ? ' is-invalid' : '')
              }
              value={formik.values.team}
              onChange={formik.handleChange}
              id="team"
            >
              <option value="">Select Team</option>
              {team &&
                team.map((item, key) => (
                  <option value={item?.id} key={key}>
                    {item?.name} ({item?.sn})
                  </option>
                ))}
            </CFormSelect>
            {formik.errors.team && formik.touched.team && (
              <CFormFeedback invalid>{formik.errors.team}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="position">Position *</CFormLabel>
            <CFormSelect
              aria-label="select position"
              name="position"
              className={
                'form-control' +
                (formik.errors.position && formik.touched.position ? ' is-invalid' : '')
              }
              value={formik.values.position}
              onChange={formik.handleChange}
              id="position"
            >
              <option value="">Select Position</option>
              {position &&
                position.map((item, key) => (
                  <option value={item?.id} key={key}>
                    {item?.name}
                  </option>
                ))}
            </CFormSelect>
            {formik.errors.position && formik.touched.position && (
              <CFormFeedback invalid>{formik.errors.position}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="value">Value *</CFormLabel>
            <CFormSelect
              aria-label="Select Value"
              name="value"
              className={
                'form-control' + (formik.errors.value && formik.touched.value ? ' is-invalid' : '')
              }
              value={formik.values.value}
              onChange={formik.handleChange}
              id="value"
            >
              <option value="">Select Value</option>
              {playerValue &&
                playerValue.map((item, key) => (
                  <option value={item?.price} key={key}>
                    {item?.price_name}
                  </option>
                ))}
            </CFormSelect>
            {formik.errors.value && formik.touched.value && (
              <CFormFeedback invalid>{formik.errors.value}</CFormFeedback>
            )}
          </CCol>

          <CCol xs={12}>
            <hr className="mt-1 mb-0" />
            <h6 className="fw-bold mb-0 mt-3">Optional Details</h6>
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="bat_style">Bat Style</CFormLabel>
            <CFormSelect
              aria-label="Select Bat Style"
              name="bat_style"
              value={formik.values.bat_style}
              onChange={formik.handleChange}
              id="bat_style"
            >
              <option value="">Not set</option>
              {batStyle &&
                batStyle.map((item, key) => (
                  <option value={item?.id} key={key}>
                    {item?.name}
                  </option>
                ))}
            </CFormSelect>
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="bowl_style">Bowl Style</CFormLabel>
            <CFormSelect
              aria-label="Select Bowl Style"
              name="bowl_style"
              value={formik.values.bowl_style}
              onChange={formik.handleChange}
              id="bowl_style"
            >
              <option value="">Not set</option>
              {bowlStyle &&
                bowlStyle.map((item, key) => (
                  <option value={item?.id} key={key}>
                    {item?.name}
                  </option>
                ))}
            </CFormSelect>
          </CCol>
          <CCol xs={12}>
            <CFormLabel>Profile Image</CFormLabel>
            <CRow className="g-0">
              <CCol xs="auto">
                <CButtonGroup role="group">
                  <CButton
                    color="primary"
                    variant={imageMode === 'avatar' ? undefined : 'outline'}
                    onClick={() => setImageMode('avatar')}
                  >
                    Avatar
                  </CButton>
                  <CButton
                    color="primary"
                    variant={imageMode === 'upload' ? undefined : 'outline'}
                    onClick={() => setImageMode('upload')}
                  >
                    Upload Image
                  </CButton>
                </CButtonGroup>
              </CCol>
            </CRow>
            {imageMode === 'upload' ? (
              <>
                <CFormInput
                  type="file"
                  id="formFile"
                  name="image"
                  className="mt-2"
                  onChange={(event) => {
                    formik.setTouched({ ...formik.touched, image: true })
                    formik.setFieldValue('image', event.target.files[0])
                  }}
                />
                {formik.values.image ? (
                  <PreviewImage
                    className={{ margin: 'auto' }}
                    width={80}
                    height={80}
                    file={formik.values.image}
                  />
                ) : null}
                {formik.touched.image && formik.errors.image ? (
                  <CFormFeedback invalid className="d-block">
                    {formik.errors.image}
                  </CFormFeedback>
                ) : null}
              </>
            ) : (
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-body-secondary mt-2"
                style={{ width: 64, height: 64 }}
              >
                <CIcon icon={cilUser} size="xl" />
              </div>
            )}
          </CCol>
          <CCol xs={12}>
            <CFormSwitch
              id="is_active"
              label="Active — available for fantasy selection"
              checked={formik.values.is_active}
              onChange={(e) => formik.setFieldValue('is_active', e.target.checked)}
            />
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <div className="d-flex gap-2">
          <CLoadingButton
            color="secondary"
            variant="outline"
            loading={loader}
            onClick={() => submitForm(true)}
          >
            Save &amp; Add Another
          </CLoadingButton>
          <CLoadingButton color="primary" loading={loader} onClick={() => submitForm(false)}>
            Create Player
          </CLoadingButton>
        </div>
      </div>
    </COffcanvas>
  )
}

export default AddForm
